import React, { useState, useEffect, useRef } from 'react';
import './header.css';
import { useAuth } from '../services/AuthContext.js';
import { Notifications } from './notifications.js';
import {
  X, ChevronDown, ChevronRight, ChevronUp, Home, Info, BookOpen, Cpu,
  Users, FileCheck, UserCircle, Hammer, LayoutDashboard, Library,
  MessageSquare, MessageCircle, Zap, LogIn, UserPlus, LogOut, ArrowUpCircle,
  MessageCircleQuestion, GraduationCap, FileText, Mail
} from 'lucide-react';

const baseAPI_URL = process.env.REACT_APP_BACKEND_API_BASE_ENDPOINT;

function getNomeCurto(user) {
  const nomeCompleto = user.attributes?.name || user.getUsername();
  const primeiroNome = nomeCompleto.split(' ')[0];
  return primeiroNome.length > 12 ? primeiroNome.slice(0, 12) + '…' : primeiroNome;
}

const publicLinks = [
  { label: 'Início', href: '/', icon: <Home size={18} /> },
  { label: 'Sobre', href: '/about', icon: <Info size={18} /> },
  /* { label: 'Conteúdo', href: '/conteudo', icon: <BookOpen size={18} /> },
  { label: 'Circuitos', href: '/circuitos', icon: <Cpu size={18} /> },
  { label: 'Avaliações', href: '/avaliacoes', icon: <Users size={18} /> },
  { label: 'Requisitos', href: '/requisitos', icon: <FileCheck size={18} /> },
  { label: 'Teoria', href: '/teoria', icon: <Lightbulb size={18} /> },
  { label: 'Alunos Antigos', href: '/antigos', icon: <GraduationCap size={18} /> },
  { label: 'Prática', href: '/pratica', icon: <Hammer size={18} /> }, */
  { label: 'FAQ', href: '/duvidas', icon: <MessageCircleQuestion size={18} /> },
  { label: 'Blog', href: '/blog', icon: <FileText size={18} /> },
  { label: 'Contato', href: '/contact', icon: <Mail size={18} /> },
];

const courses = [
  { label: 'Domínio Elétrico (conteúdo principal)', href: '/public-course-page/curso-dominio-eletrico', icon: <BookOpen size={18} className="icon-title"/> },
  { label: 'Domínio Elétrico Labs', href: '/public-course-page/dominio-eletrico-labs', icon: <BookOpen size={18} className="icon-title"/> },
  { label: 'Matemática do Elétron', href: '/public-course-page/matematica-do-eletron', icon: <BookOpen size={18} className="icon-title"/> },
  { label: 'Playlist de Introdução aos Circuitos Elétricos', href: '/public-course-page/playlist-introducao-circuitos-eletricos', icon: <BookOpen size={18} className="icon-title"/> },
  { label: 'Como Estudar Melhor na Engenharia', href: '/public-course-page/como-estudar-melhor-na-engenharia', icon: <BookOpen size={18} className="icon-title"/> },
  { label: 'Erros Elétricos', href: '/public-course-page/erros-eletricos', icon: <BookOpen size={18} className="icon-title"/> },
  { label: 'Exercícios Resolvidos 2020', href: '/public-course-page/exercicios-resolvidos-2020', icon: <BookOpen size={18} className="icon-title"/> },
  { label: 'Exercícios Resolvidos 2022', href: '/public-course-page/exercicios-resolvidos-2022', icon: <BookOpen size={18} className="icon-title"/> },
  { label: 'Exercícios Resolvidos 2023', href: '/public-course-page/exercicios-resolvidos-2023', icon: <BookOpen size={18} className="icon-title"/> },
  { label: 'Lives Canal do Elétron', href: '/public-course-page/lives-canal-do-eletron', icon: <BookOpen size={18} className="icon-title"/> },
  { label: 'Multivibrador Astável', href: '/public-course-page/multivibrador-astavel', icon: <BookOpen size={18} className="icon-title"/> },
  { label: 'Vídeos de Experimentos', href: '/public-course-page/videos-de-experimentos', icon: <BookOpen size={18} className="icon-title"/> }
];

const loggedLinks = [
  { label: 'Dashboard', href: '/dashboard', icon: <LayoutDashboard size={18} /> },
  { label: 'Cursos', href: '/allcourses', icon: <Library size={18} /> },
  { label: 'News', href: '/news', icon: <MessageSquare size={18} /> },
  { label: 'Rede', href: '/network', icon: <Users size={18} /> },
  { label: 'Chats', href: '/chats', icon: <MessageCircle size={18} /> },
  { label: 'Sua conta', href: '/account', icon: <UserCircle size={18} /> },
];

export function Header({ isMobile }) {
  const [menuOpen, setMenuOpen] = useState(false);
  
  const [coursesOpen, setCoursesOpen] = useState(false);
  const [loggedOpen, setLoggedOpen] = useState(false);
  const [accountType, setAccountType] = useState('unknown');
  const { user, getIdToken } = useAuth();
  const coursesRef = useRef(null);
  const loggedRef = useRef(null);

  const toggleMenu = () => setMenuOpen(prev => !prev);
  const toggleCourses = () => setCoursesOpen(prev => !prev);
  const toggleLogged = () => setLoggedOpen(prev => !prev);

  const fetchAccountType = async () => {
      if (!user) {
        return;
      }
      try {
        const token = await getIdToken();     
        const res = await fetch(`${baseAPI_URL}/users/get-user-data`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        const data = await res.json(); // assume it's a list of course IDs
        setAccountType(data.account_type);
      } catch (err) {
        console.error("Failed to fetch user data", err);
      }
    };
    
    useEffect(() => {
      fetchAccountType();
    }, [user]);

  // Close logged dropdown when clicking outside (desktop)
  useEffect(() => {
    function handleClickOutside(event) {
      if (loggedRef.current && !loggedRef.current.contains(event.target)) {
        setLoggedOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Pin header to top when mobile menu open
  useEffect(() => {
    const header = document.querySelector('.header');
    if (header) {
      if (menuOpen) header.classList.add('fixed-top');
      else header.classList.remove('fixed-top');
    }
  }, [menuOpen]);

  let authLinks;

  if (user && accountType === 'free') {
    authLinks = [
      {
        label: `${getNomeCurto(user)} (sair)`,
        href: '/logon',
        type: 'logout',
        icon: <LogOut size={18} />,
      },
      {
        label: 'Premium',
        href: '/premium',
        type: 'upgrade',
        icon: <ArrowUpCircle size={18} />,
      },
    ];
  } else if (user) {
      authLinks = [
      {
        label: `${getNomeCurto(user)} (sair)`,
        href: '/logon',
        type: 'logout',
        icon: <LogOut size={18} />,
      },
    ];
  } 
  else {
    authLinks = [
      { label: 'Login', href: '/logon', type: 'login', icon: <LogIn size={18} /> },
      // { label: 'Criar conta', href: '/signup?utm_source=menu', type: 'signup', icon: <UserPlus size={18} /> },
    ];
  }

  if (isMobile) {
    return (
      <header className="header">
        <div className="logo-container">
          <a href="/" title="Domínio Elétrico" className="logo-link">
            <img
              src="/dominio_eletrico_logo_2023_square_fundo_transparente.png"
              alt="Logo do Domínio Elétrico"
              className="logo"
            />
          </a>
        </div>

        <nav className="nav">
          <Notifications />
          <button onClick={toggleMenu} className="menu-button" aria-expanded={menuOpen}>
            <span className="menu-label">
              Menu {menuOpen ? <X size={22} /> : <ChevronDown size={22} />}
            </span>
          </button>

          {menuOpen && (
            <>
            <ul className="dropdown" role="menu">
              <div className="mobile-auth-buttons">
              {authLinks.map(link => (
                <a
                  key={link.href}
                  href={link.href}
                  className={`auth-button mobile-auth-button ${link.type}`}
                >
                  {link.icon} {link.label}
                </a>
              ))}
            </div>
              {publicLinks.map(link => (
                <li key={link.href}>
                  <a href={link.href} className="link" role="menuitem">
                    {link.icon} {link.label}
                  </a>
                </li>
              ))}

              <li className="menu-title-collapsible">
                <div className="menu-title-row">
                  <a href="/allcourses" className="link">Cursos</a>
                  <button
                    onClick={toggleCourses}
                    className="collapse-toggle"
                    aria-label={coursesOpen ? 'Recolher cursos' : 'Expandir cursos'}
                    aria-expanded={coursesOpen}
                  >
                    {coursesOpen ? <ChevronUp size={18} /> : <ChevronRight size={18} />}
                  </button>
                </div>
                {coursesOpen && (
                  <ul className="submenu">
                    {courses.map(link => (
                      <li key={link.href}>
                        <a href={link.href} className="link internal-link">
                          {link.icon} {link.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </li>

              <li className="menu-title-collapsible">
                <div className="menu-title-row">
                  <a href="/dashboard" className="link">Área logada</a>
                  <button
                    onClick={toggleLogged}
                    className="collapse-toggle"
                    aria-label={loggedOpen ? 'Recolher área logada' : 'Expandir área logada'}
                    aria-expanded={loggedOpen}
                  >
                    {loggedOpen ? <ChevronUp size={18} /> : <ChevronRight size={18} />}
                  </button>
                </div>

                {loggedOpen && (
                  <ul className="submenu">
                    {loggedLinks.map(link => (
                      <li key={link.href}>
                        <a href={link.href} className="link internal-link">
                          {link.icon} {link.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            </ul>
          </>)}
        </nav>
      </header>
    );
  }

  // DESKTOP
  return (
    <header className="header header-desktop">
      <div className="logo-container">
        <a href="/" title="Domínio Elétrico" className="logo-link">
          <img
            src="/dominio_eletrico_logo_2023_square_fundo_transparente.png"
            alt="Logo do Domínio Elétrico"
            className="logo"
          />
        </a>
      </div>

      <nav className="nav public-nav">
        {publicLinks.map(link => (
          <a key={link.href} href={link.href} className="menu-button-desktop">
            {link.icon} {link.label}
          </a>
        ))}
      </nav>

      <nav className="nav internal-nav" ref={loggedRef}>
        <div className={`dropdown-container ${coursesOpen ? 'open' : ''}`}>
          <button
            type="button"
            className="menu-button-desktop internal-link dropdown-toggle-btn"
            onClick={toggleCourses}
            aria-expanded={coursesOpen}
            aria-haspopup="true"
          >
            Cursos <ChevronDown size={16} />
          </button>

          <div className={`dropdown-menu ${coursesOpen ? 'visible' : ''}`} role="menu">
            {courses.map(link => (
              <a key={link.href} href={link.href} className="dropdown-item" role="menuitem">
                {link.icon} {link.label}
              </a>
            ))}
          </div>
        </div>

        <Notifications />

        <div className={`dropdown-container ${loggedOpen ? 'open' : ''}`}>
          <button
            type="button"
            className="menu-button-desktop internal-link dropdown-toggle-btn"
            onClick={toggleLogged}
            aria-expanded={loggedOpen}
            aria-haspopup="true"
          >
            Área logada <ChevronDown size={16} />
          </button>

          <div className={`dropdown-menu ${loggedOpen ? 'visible' : ''}`} role="menu">
            {loggedLinks.map(link => (
              <a key={link.href} href={link.href} className="dropdown-item" role="menuitem">
                {link.icon} {link.label}
              </a>
            ))}
          </div>
        </div>

        {authLinks.map(link => (
          <a
            key={link.href}
            href={link.href}
            className={`menu-button-desktop auth-button ${link.type}`}
          >
            {link.icon} {link.label}
          </a>
        ))}
      </nav>
    </header>
  );
}
